import type { QueryClient } from '@tanstack/react-query'
import { authApi } from '../api'
import { AUTH_USER_KEY } from '../authRedirect'
import { clearGrantMirror } from '../grantMirror'
import { getPendingCalls, getPendingVisitCount, getRouteOutcomes } from '../offlineStore'
import { clearInstance } from '../native/instance'
import { clearCredential, getCredential, setActiveMode } from '../native/credentialStore'
import { dropRejectedGrant } from '../native/wipeAckCredential'
import { clearCachedPublicConfig } from '../runtimeConfig'

/**
 * The one organizer sign-out (replaces the three duplicated layout handlers). Web: the server
 * clears the cookie and bumps TokenVersion; native (NB1-7): it revokes only this device session,
 * then the `od_` credential is dropped locally. `clearCredential` is a no-op on web.
 */
export async function signOutOrganizer(): Promise<void> {
  await authApi.logout().catch(() => {})
  await clearCredential('organizer').catch(() => {})
  localStorage.removeItem(AUTH_USER_KEY)
  window.location.replace('/login')
}

/**
 * NB1-13: switching organization is blocked while a grant credential exists or ANY offline queue
 * still holds work — queued visits, unsynced route outcomes (drop-offs) or queued calls, whichever
 * grant recorded them (after an expiry the mirror is gone, so a current-grant filter would miss
 * them; and after a switch they would drain to the OTHER org's server and be rejected for good).
 * There is NO silent wipe (mirrors A1's "different code" rule). A queue that cannot be read fails
 * closed (blocked): an unreadable queue is not proof that nothing is unsynced.
 */
export async function canSwitchOrganization(): Promise<boolean> {
  if (getCredential('grant')) return false
  try {
    if ((await getPendingVisitCount()) > 0) return false
    if ((await getRouteOutcomes()).some((o) => !o.synced)) return false
    return (await getPendingCalls()).length === 0
  } catch {
    return false
  }
}

export const SWITCH_BLOCKED_MESSAGE = 'Sign out of your volunteer session first so nothing unsynced is lost.'

/**
 * NB1-13 permitted switch, run BEFORE a new instance is chosen. Checks `canSwitchOrganization`
 * first and touches nothing when blocked ('blocked'). Otherwise, for the old instance: revokes and
 * clears the organizer credential, drops the `auth_user` / `canvasser_grant` mirrors, drops every
 * cached query (they all belong to the old server — the public config included), resets the
 * active mode (it is global, not per-instance, so the next instance must not start in 'grant'
 * mode with no grant credential), and clears the selected instance.
 */
export async function beginOrganizationSwitch(qc?: QueryClient): Promise<'blocked' | 'switched'> {
  if (!(await canSwitchOrganization())) return 'blocked'
  if (getCredential('organizer')) await authApi.logout().catch(() => {})
  await clearCredential('organizer').catch(() => {})
  dropRejectedGrant() // [LEGAL] the old instance's retained wipe-ack credential never crosses over
  localStorage.removeItem(AUTH_USER_KEY)
  clearGrantMirror()
  qc?.clear()
  await setActiveMode('organizer').catch(() => {})
  await clearCachedPublicConfig()
  await clearInstance()
  return 'switched'
}
